import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiService } from '../../services/api';
import { KGInstance } from '../../types';
import { extractErrorMessage } from '../../utils/errorHandler';
import { formatDateTime } from '../../utils/dateUtils';
import CreateInstanceModal from './CreateInstanceModal';
import InstanceLogs from './InstanceLogs';
import '../shared.css';
import './InstanceList.css';

const POLL_INTERVAL_MS = 10_000;

interface InstanceListProps {
  kgId: string;
  versionId: string;
  onRunningCount?: (count: number) => void;
}

const InstanceList: React.FC<InstanceListProps> = ({ kgId, versionId, onRunningCount }) => {
  const [instances, setInstances] = useState<KGInstance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState<{ [key: string]: boolean }>({});
  const [showLogsForInstance, setShowLogsForInstance] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const abortRef = useRef<AbortController | null>(null);

  const loadInstances = useCallback(async (showLoadingState: boolean = true) => {
    // Cancel any in-flight status detail requests from the previous call
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;

    try {
      if (showLoadingState) setLoading(true);
      setError('');

      const response: any = await apiService.listInstances(kgId, versionId);
      if (signal.aborted) return;

      if (response.data) {
        const instanceList: KGInstance[] = response.data.map((item: any) => ({
          instance_id: item.instance_id,
          kg_id: item.kg_id || kgId,
          version_id: item.version_id || versionId,
          instance_type: item.instance_type,
          status: item.status,
          is_terminated: item.is_terminated ?? false,
          default_instance: item.default_instance ?? false,
          created_at: item.created_at,
          org_id: item.org_id,
          status_response: item.status_response,
        }));

        setInstances(instanceList);

        const runningCount = instanceList.filter(
          (i) => i.status?.toLowerCase() === 'running' && !i.is_terminated
        ).length;
        onRunningCount?.(runningCount);

        // Fetch detailed status for running instances — guarded by AbortSignal
        const runningInstances = instanceList.filter(
          (i) => i.status?.toLowerCase() === 'running' && !i.is_terminated
        );
        await Promise.allSettled(
          runningInstances.map(async (instance) => {
            if (signal.aborted) return;
            try {
              const statusResponse: any = await apiService.getInstanceStatus(
                kgId,
                versionId,
                instance.instance_id
              );
              if (signal.aborted) return;
              if (statusResponse.data?.attributes?.status_response) {
                setInstances(prev =>
                  prev.map(inst =>
                    inst.instance_id === instance.instance_id
                      ? { ...inst, status_response: statusResponse.data.attributes.status_response }
                      : inst
                  )
                );
              }
            } catch (err) {
              if (!signal.aborted) {
                console.error(`Error fetching status for instance ${instance.instance_id}:`, err);
              }
            }
          })
        );
      }
    } catch (err: any) {
      if (signal.aborted) return;
      setError(extractErrorMessage(err, 'Failed to load instances'));
    } finally {
      if (!signal.aborted && showLoadingState) setLoading(false);
    }
  }, [kgId, versionId, onRunningCount]);

  useEffect(() => {
    loadInstances(true);
    const interval = setInterval(() => loadInstances(false), POLL_INTERVAL_MS);
    return () => {
      clearInterval(interval);
      abortRef.current?.abort();
    };
  }, [loadInstances]);

  const handleStatusChange = async (
    instanceId: string,
    desiredStatus: 'RUNNING' | 'STOPPED' | 'TERMINATED'
  ) => {
    setActionError('');
    try {
      setUpdatingStatus(prev => ({ ...prev, [instanceId]: true }));
      await apiService.updateInstanceStatus(kgId, versionId, instanceId, desiredStatus);

      setInstances(prev =>
        prev.map(inst =>
          inst.instance_id === instanceId
            ? { ...inst, status: desiredStatus.toLowerCase(), is_terminated: desiredStatus === 'TERMINATED' }
            : inst
        )
      );

      setTimeout(() => loadInstances(false), 2000);
    } catch (err: any) {
      setActionError(extractErrorMessage(err, 'Failed to update instance status'));
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [instanceId]: false }));
    }
  };

  const handleSetDefault = async (instanceId: string) => {
    setActionError('');
    try {
      setUpdatingStatus(prev => ({ ...prev, [instanceId]: true }));
      await apiService.setDefaultInstance(kgId, versionId, instanceId);
      setInstances(prev =>
        prev.map(inst => ({ ...inst, default_instance: inst.instance_id === instanceId }))
      );
    } catch (err: any) {
      setActionError(extractErrorMessage(err, 'Failed to set default instance'));
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [instanceId]: false }));
    }
  };

  const handleInstanceCreated = () => {
    setShowCreateModal(false);
    loadInstances();
  };

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'RUNNING':    return 'status-running';
      case 'STOPPED':    return 'status-stopped';
      case 'TERMINATED': return 'status-terminated';
      case 'PENDING':    return 'status-pending';
      default:           return 'status-unknown';
    }
  };

  const filteredInstances = instances.filter((instance) => {
    const s = searchTerm.toLowerCase();
    return (
      instance.instance_id.toLowerCase().includes(s) ||
      instance.instance_type?.toLowerCase().includes(s) ||
      instance.status?.toLowerCase().includes(s)
    );
  });

  return (
    <div className="instance-list tab-root">
      <div className="tab-topbar">
        <div className="tab-topbar-left">
          <input
            type="text"
            className="tab-search"
            placeholder="Search instances…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {!loading && (
            <span className="tab-stat-pill">
              {filteredInstances.length} of {instances.length}
            </span>
          )}
        </div>
        <div className="tab-topbar-right">
          <button className="tab-btn tab-btn--primary" onClick={() => setShowCreateModal(true)}>
            + Launch Instance
          </button>
        </div>
      </div>

      {error && (
        <div className="tab-banner tab-banner--error">
          <span className="tab-banner-icon">⚠</span>
          <span className="tab-banner-body">{error}</span>
        </div>
      )}

      {actionError && (
        <div className="tab-banner tab-banner--error">
          <span className="tab-banner-icon">⚠</span>
          <span className="tab-banner-body">{actionError}</span>
          <button className="tab-banner-close" onClick={() => setActionError('')}>✕</button>
        </div>
      )}

      {loading ? (
        <div className="tab-loader">
          <span className="tab-spinner" />
        </div>
      ) : instances.length === 0 ? (
        <div className="tab-empty">
          <div className="tab-empty-icon">⬡</div>
          <div className="tab-empty-title">No instances yet</div>
          <div className="tab-empty-body">Launch an instance to start querying this knowledge graph.</div>
        </div>
      ) : filteredInstances.length === 0 ? (
        <div className="tab-empty">
          <div className="tab-empty-icon">⌕</div>
          <div className="tab-empty-title">No matches</div>
          <div className="tab-empty-body">No instances match "{searchTerm}"</div>
        </div>
      ) : (
        <div className="instances-grid">
          {filteredInstances.map((instance) => (
            <div key={instance.instance_id} className="instance-card">
              <div className="instance-header">
                <div className="instance-title">
                  <h3>{instance.instance_id}</h3>
                </div>
                <div className="instance-badges">
                  {instance.default_instance && (
                    <span className="default-badge">Default</span>
                  )}
                  <span className={`status-badge ${getStatusColor(instance.status)}`}>
                    {instance.status}
                  </span>
                </div>
              </div>

              <div className="instance-info">
                {instance.instance_type && (
                  <div className="info-row">
                    <span className="info-label">Type</span>
                    <span className="instance-type-tag" title={instance.instance_type}>
                      {instance.instance_type}
                    </span>
                  </div>
                )}
                <div className="info-row">
                  <span className="info-label">Created</span>
                  <span className="info-value">{formatDateTime(instance.created_at)}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">DB Status</span>
                  {instance.status?.toLowerCase() !== 'running' ? (
                    <span className="info-value db-status-inactive">—</span>
                  ) : !instance.status_response ? (
                    <span className="info-value db-status-checking">Checking…</span>
                  ) : instance.status_response.error ? (
                    <span className="info-value db-status-error" title={instance.status_response.error}>
                      Connection Error
                    </span>
                  ) : instance.status_response.message ? (
                    <span className="info-value db-status-success">
                      {instance.status_response.message}
                    </span>
                  ) : (
                    <span className="info-value db-status-inactive">—</span>
                  )}
                </div>
              </div>

              <div className="instance-actions">
                <button
                  className="tab-btn tab-btn--ghost"
                  onClick={() => setShowLogsForInstance(instance.instance_id)}
                >
                  Logs
                </button>
                {!instance.default_instance && !instance.is_terminated && (
                  <button
                    className="tab-btn tab-btn--ghost"
                    onClick={() => handleSetDefault(instance.instance_id)}
                    disabled={updatingStatus[instance.instance_id]}
                  >
                    {updatingStatus[instance.instance_id] ? 'Setting…' : 'Set Default'}
                  </button>
                )}
                {!instance.is_terminated && (
                  <>
                    {instance.status?.toLowerCase() === 'stopped' && (
                      <button
                        className="tab-btn tab-btn--primary"
                        onClick={() => handleStatusChange(instance.instance_id, 'RUNNING')}
                        disabled={updatingStatus[instance.instance_id]}
                      >
                        {updatingStatus[instance.instance_id] ? 'Starting…' : 'Start'}
                      </button>
                    )}
                    {instance.status?.toLowerCase() === 'running' && (
                      <button
                        className="tab-btn tab-btn--ghost"
                        onClick={() => handleStatusChange(instance.instance_id, 'STOPPED')}
                        disabled={updatingStatus[instance.instance_id]}
                      >
                        {updatingStatus[instance.instance_id] ? 'Stopping…' : 'Stop'}
                      </button>
                    )}
                    <button
                      className="tab-btn tab-btn--danger"
                      onClick={() => handleStatusChange(instance.instance_id, 'TERMINATED')}
                      disabled={updatingStatus[instance.instance_id]}
                    >
                      {updatingStatus[instance.instance_id] ? 'Terminating…' : 'Terminate'}
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreateModal && (
        <CreateInstanceModal
          kgId={kgId}
          versionId={versionId}
          onClose={() => setShowCreateModal(false)}
          onCreated={handleInstanceCreated}
        />
      )}

      {showLogsForInstance && (
        <InstanceLogs
          kgId={kgId}
          versionId={versionId}
          instanceId={showLogsForInstance}
          onClose={() => setShowLogsForInstance(null)}
        />
      )}
    </div>
  );
};

export default InstanceList;
